import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { JSONValue } from "shared/types/common";

import { getAvatarThumbnailSrc } from "shared/components/Avatar";
import { WizardProfileType } from "shared/components/WizardNew/types";

import { IS_ONLINE } from "shared/constants";

import { useTrigger } from "shared/hooks/useTrigger";

import { likeUser } from "shared/services/user";

import styles from "@platformAssets/components/Wizard.module.scss";

type Props = {
  handleLikedSomeProfile: (uid: number) => void;
  item: WizardProfileType;
};

const WizardProfile = ({ item, handleLikedSomeProfile }: Props) => {
  const { t } = useTranslation("wizardNew");

  const [liked, setLiked] = useState(false);
  const [isAnimate, showAnimate, hideAnimate] = useTrigger(false);
  const [animationData, setAnimationData] = useState<JSONValue | null>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (isAnimate && lottieRef.current) {
      lottieRef.current.goToAndStop(0, true);
      lottieRef.current.play();
    }
  }, [isAnimate]);

  /**
   * Handle click on like button
   */
  const handleClickLike = async () => {
    if (liked) {
      return;
    }

    if (!animationData) {
      await import("assets/shared/lotties/heart-boom.json").then((data) => {
        setAnimationData(data.default);
      });
    }

    if (typeof showAnimate === "function") {
      showAnimate();
    }

    handleLikedSomeProfile(item.uid);
    setLiked(true);

    await likeUser({ uid: item.uid });
  };

  return (
    <div className={styles.wizardCard}>
      <div
        className={styles.wizardAvatar}
        style={{
          backgroundImage: `url("${getAvatarThumbnailSrc(item.avatar)}")`,
        }}
      />

      <div className={styles.wizardInfo}>
        <div
          className={cn(styles.wizardName, {
            [styles.wizardNameOnline]: item.is_online === IS_ONLINE,
          })}
        >
          <span>{item.username}</span>
          <div>{`, ${item.age}`}</div>
        </div>

        <button
          className={cn(styles.wizardLikeButton, { [styles.liked]: liked })}
          onClick={handleClickLike}
          disabled={liked}
        >
          <div className={cn(styles.like, { [styles.liked]: liked })}>
            <div
              className={cn(styles.likeAnimation, {
                [styles.hideAnimation]: !isAnimate,
              })}
            >
              <Lottie
                loop={false}
                lottieRef={lottieRef}
                animationData={animationData}
                autoplay
                rendererSettings={{
                  preserveAspectRatio: "xMidYMid slice",
                }}
                onComplete={() => {
                  typeof hideAnimate === "function" ? hideAnimate() : null;
                }}
              />
            </div>
          </div>

          <div>{liked ? t("profiles.liked") : t("profiles.like")}</div>
        </button>
      </div>
    </div>
  );
};

export { WizardProfile };
